import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Outlet,
  useNavigate,
  useParams,
  useResolvedPath,
} from 'react-router-dom'
import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { camelCase, find, isEmpty, map } from 'lodash'

import { Progress } from '../api/index'
import { REACT_APP_DOMAIN, SIGNALR_URL } from '../common/constants'
import AppSpinner from '../components/AppSpinner'
import NavList from '../components/NavList'
import NavMobileHeader from '../components/NavMobileHeader'
import useAuth from '../hooks/useAuth'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import {
  AccessoriesCheckedIcon,
  AccessoriesIcon,
  BodyCheckedIcon,
  BodyIcon,
  CheckCheckedIcon,
  CheckIcon,
  CommentCheckedIcon,
  CommentIcon,
  HourglassCheckedIcon,
  HourglassIcon,
  InteriorCheckedIcon,
  InteriorIcon,
  ShieldCheckedIcon,
  ShieldIcon,
  TiresCheckedIcon,
  TiresIcon,
} from '../icons'
import CarFrontIcon from '../icons/CarFrontIcon'
import CarFrontIconChecked from '../icons/CarFrontIconChecked'

const STEPS_ASSETS = [
  {
    name: 'carBasicData',
    nameInBackEndProgressResponse: 'carBasicData',
    icon: <CarFrontIcon />,
    checkedIcon: <CarFrontIconChecked />,
    maxProgress: 10,
  },
  {
    name: 'guarantee',
    nameInBackEndProgressResponse: 'guaratee',
    icon: <ShieldIcon />,
    checkedIcon: <ShieldCheckedIcon />,
    maxProgress: 5,
  },
  {
    name: 'reviewHistory',
    nameInBackEndProgressResponse: 'reviewHistory',
    icon: <HourglassIcon />,
    checkedIcon: <HourglassCheckedIcon />,
    maxProgress: 5,
  },
  {
    name: 'bodyCondition',
    nameInBackEndProgressResponse: 'bodyCondition',
    icon: <BodyIcon />,
    checkedIcon: <BodyCheckedIcon />,
    maxProgress: 10,
  },
  {
    name: 'interiorCondition',
    nameInBackEndProgressResponse: 'interiorCondition',
    icon: <InteriorIcon />,
    checkedIcon: <InteriorCheckedIcon />,
    maxProgress: 5,
  },
  {
    name: 'equipment',
    nameInBackEndProgressResponse: 'equipment',
    icon: <AccessoriesIcon />,
    checkedIcon: <AccessoriesCheckedIcon />,
    maxProgress: 10,
  },
  {
    name: 'tyres',
    nameInBackEndProgressResponse: 'tyres',
    icon: <TiresIcon />,
    checkedIcon: <TiresCheckedIcon />,
    maxProgress: 10,
  },
  {
    name: 'technicalCondition',
    nameInBackEndProgressResponse: 'usedCarControl',
    icon: <CheckIcon />,
    checkedIcon: <CheckCheckedIcon />,
    maxProgress: 45,
  },
  {
    name: 'todo',
    nameInBackEndProgressResponse: 'todo',
    icon: <CommentIcon />,
    checkedIcon: <CommentCheckedIcon />,
  },
]

function ControlsEdit() {
  const currentLocation = useResolvedPath()
  const { id: controlId } = useParams()
  const { auth } = useAuth()
  const axiosPrivate = useAxiosPrivate()
  const { t } = useTranslation(['common', 'edit'])
  const [steps, setSteps] = useState([])
  const [currentStep, setCurrentStep] = useState({})
  const [connection, setConnection] = useState(null)
  const portalRef = useRef(null)
  const theme = useTheme()
  const isMobile = !useMediaQuery(theme.breakpoints.up('md'))
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    sessionStorage.setItem('digitalCertificateId', controlId)
    Progress.getProgress(axiosPrivate, controlId)
      .then(({ data }) => {
        setSteps(
          map(STEPS_ASSETS, (step) => ({
            ...step,
            label: t(`edit:menu.${camelCase(step.name)}`),
            progress: data.stepsProgress[step.nameInBackEndProgressResponse],
            to: `/controls/${controlId}/edit/${step.name}`,
          })),
        )
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }, [controlId, t, currentLocation, axiosPrivate])

  useEffect(() => {
    if (!isEmpty(steps)) {
      setCurrentStep(find(steps, { to: currentLocation.pathname }) || steps[0])
    }
  }, [currentLocation, steps])

  useEffect(() => {
    if (auth.accessToken) {
      const searchParams = new URLSearchParams({
        access_token: auth.accessToken,
      })
      const newConnection = new HubConnectionBuilder()
        .withUrl(`${SIGNALR_URL}?${searchParams.toString()}`, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .build()

      setConnection(newConnection)
    }
  }, [auth.accessToken])

  useEffect(() => {
    if (connection) {
      connection
        .start()
        .then(() => {
          connection.invoke('send', parseInt(controlId, 10))
        })
        // eslint-disable-next-line no-console
        .catch((e) => console.error(e))

      connection.on('clientMessage', () => {
        window.location.replace(`${REACT_APP_DOMAIN}/controls`)
      })
    }

    return () => {
      if (connection) {
        connection.stop()
      }
    }
  }, [connection, controlId])

  const handleConfirmDialogOpen = () => {
    setOpenConfirmDialog(true)
  }

  const handleConfirmDialogClose = () => {
    setOpenConfirmDialog(false)
  }

  const handleConfirmNavigate = () => {
    setOpenConfirmDialog(false)
    navigate('/controls')
  }

  if (isEmpty(steps) || isEmpty(currentStep) || !connection) {
    return <AppSpinner />
  }

  return (
    <Box sx={{ display: 'flex', height: 1, overflow: 'hidden' }}>
      <Grid container>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={({ palette }) => ({
              height: 1,
              py: {
                xs: 0,
                md: 3,
              },
              pl: {
                xs: 1,
                md: 5,
              },
              pr: {
                xs: 1,
                md: 3,
              },
              color: palette.gray.dark,
            })}
          >
            {isMobile ? (
              <Accordion elevation={0}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  sx={{ pl: 0 }}
                >
                  <NavMobileHeader currentStep={currentStep} />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <NavList steps={steps} currentStep={currentStep} />
                </AccordionDetails>
              </Accordion>
            ) : (
              <NavList steps={steps} currentStep={currentStep} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100vh - 175px)',
            }}
          >
            <Box sx={{ display: 'flex', flex: 1, overflow: 'auto' }}>
              <Box
                sx={{
                  px: {
                    xs: 3,
                    md: 10,
                  },
                  py: {
                    xs: 2,
                    md: 3,
                  },
                  minHeight: 'min-content',
                  width: 1,
                }}
              >
                <Outlet
                  context={{
                    controlId,
                    portalRef,
                    onCancelClick: handleConfirmDialogOpen,
                  }}
                />
              </Box>
            </Box>
            <Box
              ref={portalRef}
              sx={{
                display: 'flex',
                pt: {
                  xs: 2,
                  md: 3,
                },
                px: {
                  xs: 2,
                  md: 10,
                },
                pb: 0,
                width: 1,
                justifyContent: 'flex-end',
              }}
            ></Box>
          </Box>
        </Grid>
      </Grid>
      <Dialog
        open={openConfirmDialog}
        onClose={handleConfirmDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('common:confirmCancelTitle', 'Czy na pewno chcesz anulować?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t(
              'common:confirmCancelMessage',
              'Wszystkie niezapisane zmiany zostaną utracone.',
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} color="primary">
            {t('common:no', 'Nie')}
          </Button>
          <Button onClick={handleConfirmNavigate} color="primary" autoFocus>
            {t('common:yes', 'Tak')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ControlsEdit
